import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Group,
  Stack,
  Switch,
  Card,
  Flex,
  useMantineTheme,
  Text,
  Badge,
  Tooltip,
} from '@mantine/core';
import API from '../api';
import useConnectStore from '../store/connect';
import { SquarePlus, Webhook, FileCode } from 'lucide-react';
import ConnectionForm from '../components/forms/Connection';
import ConnectLogsSection from '../components/ConnectLogsSection';
import { SUBSCRIPTION_EVENTS } from '../constants';

const deleteConnectIntegration = (id) => {
  return API.deleteConnectIntegration(id);
};

const updateConnectIntegration = (id, values) => {
  return API.updateConnectIntegration(id, values);
};

export default function ConnectPage() {
  const { integrations, isLoading, fetchIntegrations } = useConnectStore();
  const theme = useMantineTheme();
  const [connection, setConnection] = useState(null);
  const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  const newConnection = () => {
    setConnection(null);
    setIsConnectionModalOpen(true);
  };

  const editConnection = (connection) => {
    setConnection(connection);
    setIsConnectionModalOpen(true);
  };

  const deleteConnection = async (id) => {
    console.log('Deleting connection', id);
    await deleteConnectIntegration(id);
  };

  return (
    <>
      <Box p="md" pb={120}>
        <Button
          leftSection={<SquarePlus size={18} />}
          variant="light"
          size="sm"
          onClick={() => newConnection()}
          p={10}
          color={theme.tailwind.green[5]}
          style={{
            borderWidth: '1px',
            borderColor: theme.tailwind.green[5],
            color: 'white',
          }}
        >
          New Connection
        </Button>
        {isLoading && <div>Loading...</div>}
        {!isLoading && (
          <Box
            style={{
              gap: '1rem',
              gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
              alignContent: 'start',
            }}
            display="grid"
            py={10}
          >
            {integrations.map((i) => (
              <IntegrationRow
                key={i.id}
                integration={i}
                editConnection={editConnection}
                deleteConnection={deleteConnection}
              />
            ))}
          </Box>
        )}

        <ConnectionForm
          connection={connection}
          isOpen={isConnectionModalOpen}
          onClose={() => setIsConnectionModalOpen(false)}
        />
      </Box>

      {/* Logs Section - Fixed at bottom, like SystemEvents on the Stats page */}
      <Box
        style={{
          zIndex: 100,
          pointerEvents: 'none',
        }}
        pos="fixed"
        bottom={0}
        left="var(--app-shell-navbar-width, 0)"
        right={0}
        p={'0 1rem 1rem 1rem'}
      >
        <Box style={{ pointerEvents: 'auto' }}>
          <ConnectLogsSection integrations={integrations} />
        </Box>
      </Box>
    </>
  );
}

function IntegrationRow({ integration, editConnection, deleteConnection }) {
  const type = integration.type || 'webhook';
  const [enabled, setEnabled] = useState(!!integration.enabled);
  const webhookUrl = integration?.config?.url || '';
  const scriptPath = integration?.config?.path || '';

  const toggleIntegration = async () => {
    try {
      await updateConnectIntegration(integration.id, {
        ...integration,
        enabled: !enabled,
      });
      setEnabled(!enabled);
    } catch (error) {
      console.error('Failed to update integration', error);
    }
  };

  return (
    <Card
      key={integration.id}
      shadow="sm"
      padding="md"
      radius="md"
      withBorder
      style={{
        backgroundColor: '#27272A',
      }}
      color="#fff"
      w={'100%'}
    >
      <Stack gap="xs">
        <Group justify="space-between">
          <Group align="flex-start">
            {integration.type == 'webhook' ? <Webhook /> : <FileCode />}
            <Text fw={800}>{integration.name}</Text>
          </Group>
          <Switch
            label="Enabled"
            checked={enabled}
            onChange={toggleIntegration}
          />
        </Group>

        {type === 'webhook' ? (
          <Group gap={5} align="center">
            <Text fw={500}>Target:</Text>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Tooltip label={webhookUrl} withArrow multiline>
                <Text
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {webhookUrl}
                </Text>
              </Tooltip>
            </Box>
          </Group>
        ) : (
          <Group gap={5} align="center">
            <Text fw={500}>Target:</Text>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Tooltip label={scriptPath} withArrow multiline>
                <Text
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {scriptPath}
                </Text>
              </Tooltip>
            </Box>
          </Group>
        )}

        <Text>Triggers</Text>
        <Group>
          {integration.subscriptions.map(
            (sub) =>
              sub.enabled && (
                <Badge size="sm" variant="light" color="green">
                  {SUBSCRIPTION_EVENTS[sub.event] || sub.event}
                </Badge>
              )
          )}
        </Group>
      </Stack>

      <Flex mih={50} gap="xs" justify="flex-end" align="flex-end">
        <Button size="xs" onClick={() => editConnection(integration)}>
          Edit
        </Button>
        <Button
          variant="outline"
          size="xs"
          color="red"
          onClick={() => deleteConnection(integration.id)}
        >
          Delete
        </Button>
      </Flex>
    </Card>
  );
}
